import { optimisticTranslate } from '@i18n';

import { listFromObj } from '@framework/cookbook';

/**
 * TODO: Move and categorize everything here to the ./enums folder
 * Example:
 * - common enums -> index.js
 * - whole app related enums -> app.js
 * - sections related enums -> sections.js
 * - members related enums -> members.js
 * - etc.
 */

// application level enums
export enum APPLICATION_PATHS {
    ADMINISTRATOR = 'administrator',
    EVENTS = 'events',
    LINK = 'link',
    ONBOARDING = 'onboarding',
    ORGANISATIONS = 'organisations',
    ROOMS = 'rooms',
    ROOM_CREATION = 'room-creation',
    ROOM_EDIT = 'room-edit'
}

export const APPLICATION_CONTENT_VIEWS = Object.freeze({
    EDITOR: 'EDITOR',
    ATTENDEE: 'ATTENDEE',
    ADMIN_PORTAL: 'ADMIN_PORTAL',
    ROOM_CREATION: 'ROOM_CREATION',
    ROOM_EDIT: 'ROOM_EDIT'
});

export const APPLICATION_CONTENT_ROUTE = Object.freeze({
    EDITOR: '/administrator/rooms/',
    ATTENDEE: '/rooms/',
    ADMIN_PORTAL: '/organisations/',
    ROOM_CREATION: '/room-creation/',
    ROOM_EDIT: '/room-edit/'
});

export enum APPLICATION_SECTIONS {
    BILLING = 'BILLING',
    DOCUMENTS = 'DOCUMENTS',
    FORMS = 'FORMS',
    FORMS_DETAILS = 'FORMS_DETAILS',
    HOME = 'HOME',
    MEETINGS = 'MEETINGS',
    MEETING_DETAILS = 'MEETING_DETAILS',
    MEMBERS = 'MEMBERS',
    MINUTES_EDITOR = 'MINUTES_EDITOR',
    REPORTS = 'REPORTS',
    SEARCH = 'SEARCH',
    SETTINGS = 'SETTINGS',
    SIGNATURES = 'SIGNATURES',
    TASKS_DECISIONS = 'TASKS_DECISIONS',
    TOPIC_HUB = 'TOPIC_HUB'
}

export enum ATTENDEE_SECTIONS {
    DOCUMENTS = 'ATTENDEE:DOCUMENTS',
    FORMS = 'ATTENDEE:FORMS',
    FORMS_DETAILS = 'ATTENDEE:FORMS_DETAILS',
    HOME = 'ATTENDEE:HOME',
    MEETINGS = 'ATTENDEE:MEETINGS',
    MEETING_DETAILS = 'ATTENDEE:MEETING_DETAILS',
    MINUTES_EDITOR = 'ATTENDEE:MINUTES_EDITOR',
    SEARCH = 'ATTENDEE:SEARCH',
    TASKS_DECISIONS = 'ATTENDEE:TASKS_DECISIONS',
    TOPIC_HUB = 'ATTENDEE:TOPIC_HUB'
}

export type AppSection = APPLICATION_SECTIONS | ATTENDEE_SECTIONS;

export enum SECTIONS_PATHS {
    DOCUMENTS = 'documents',
    FORMS = 'forms',
    HOME = 'home',
    MEETINGS = 'meetings',
    MEMBERS = 'members',
    SEARCH = 'search',
    SETTINGS = 'settings',
    SIGNATURE_REQUEST = 'digital-signature-request',
    TASKS_DECISIONS = 'tasks-and-decisions',
    TOPIC_HUB = 'topic-hub'
}

export const APPLICATION_URLS = Object.freeze({
    FORGOT_PASSWORD: '/password/reset/',
    LOGIN: '/'
});

export const APPLICATION_SECTIONS_SLUG_TO_PATH = Object.freeze({
    [ATTENDEE_SECTIONS.FORMS]: SECTIONS_PATHS.FORMS,
    [ATTENDEE_SECTIONS.FORMS_DETAILS]: SECTIONS_PATHS.FORMS,
    [ATTENDEE_SECTIONS.MEETINGS]: SECTIONS_PATHS.MEETINGS,
    [ATTENDEE_SECTIONS.MEETING_DETAILS]: SECTIONS_PATHS.MEETINGS,
    FORMS: SECTIONS_PATHS.FORMS,
    FORMS_DETAILS: SECTIONS_PATHS.FORMS,
    HOME: SECTIONS_PATHS.HOME,
    [ATTENDEE_SECTIONS.HOME]: SECTIONS_PATHS.HOME,
    MEETINGS: SECTIONS_PATHS.MEETINGS,
    MEETING_DETAILS: SECTIONS_PATHS.MEETINGS,
    DOCUMENTS: SECTIONS_PATHS.DOCUMENTS,
    [ATTENDEE_SECTIONS.DOCUMENTS]: SECTIONS_PATHS.DOCUMENTS,
    MEMBERS: SECTIONS_PATHS.MEMBERS,
    TASKS_DECISIONS: SECTIONS_PATHS.TASKS_DECISIONS,
    [ATTENDEE_SECTIONS.TASKS_DECISIONS]: SECTIONS_PATHS.TASKS_DECISIONS,
    TOPIC_HUB: SECTIONS_PATHS.TOPIC_HUB,
    [ATTENDEE_SECTIONS.TOPIC_HUB]: SECTIONS_PATHS.TOPIC_HUB,
    [APPLICATION_SECTIONS.SEARCH]: SECTIONS_PATHS.SEARCH
});

export enum ADMIN_PORTAL_SECTION_SLUG {
    ADMINPORTAL = 'adminportal',
    BILLING = 'billing',
    MEMBERS = 'members',
    REPORTS = 'reports',
    SETTINGS = 'settings'
}

export enum Mode {
    ADMIN_PORTAL = 'admin portal mode',
    EDIT = 'edit mode',
    VIEW = 'view mode'
}

export const ADMIN_PORTAL_SECTION_LABEL_BY_SLUG = (): Readonly<{
    [ADMIN_PORTAL_SECTION_SLUG.ADMINPORTAL]: string;
    [ADMIN_PORTAL_SECTION_SLUG.MEMBERS]: string;
    [ADMIN_PORTAL_SECTION_SLUG.REPORTS]: string;
    [ADMIN_PORTAL_SECTION_SLUG.BILLING]: string;
    [ADMIN_PORTAL_SECTION_SLUG.SETTINGS]: string;
}> => ({
    [ADMIN_PORTAL_SECTION_SLUG.ADMINPORTAL]: optimisticTranslate('Admin portal'),
    [ADMIN_PORTAL_SECTION_SLUG.MEMBERS]: optimisticTranslate('Users'),
    [ADMIN_PORTAL_SECTION_SLUG.REPORTS]: optimisticTranslate('Reports'),
    [ADMIN_PORTAL_SECTION_SLUG.BILLING]: optimisticTranslate('Accounting'),
    [ADMIN_PORTAL_SECTION_SLUG.SETTINGS]: optimisticTranslate('Settings')
});

export const RESTRICTION_STATUS = Object.freeze({
    RESTRICTED: 'RESTRICTED',
    ALLOWED: 'ALLOWED'
});

// app specific enums
export const EVENT_TYPES = Object.freeze({
    MEETING: 1,
    CONFERENCE_CALL: 2,
    CIRCULAR_RESOLUTION: 3
});

export const MEMBER_ROLES = Object.freeze({
    MEMBER: 1,
    ASSISTANT: 2,
    ADMIN: 3,
    AUDITOR: 4,
    OFFICE_MANAGER: 5,
    USER_MANAGEMENT_ADMIN: 6,
    CONTRIBUTOR: 7
});

export const MEMBER_ROLE_BY_ID = Object.freeze({
    1: 'member',
    2: 'assistant',
    3: 'admin',
    4: 'auditor',
    5: 'officemanager',
    6: 'usermanagementadmin',
    7: 'contributor'
});

export const MEMBER_ROLE_LABEL_BY_ID = (): Readonly<{
    1: string;
    2: string;
    3: string;
    4: string;
    5: string;
    6: string;
    7: string;
}> =>
    Object.freeze({
        1: optimisticTranslate('Member'),
        2: optimisticTranslate('Assistant'),
        3: optimisticTranslate('Admin'),
        4: optimisticTranslate('Auditor'),
        5: optimisticTranslate('Office Manager'),
        6: optimisticTranslate('User management admin'),
        7: optimisticTranslate('Contributor')
    });

export const MEMBER_CLASSIFICATION_LEVELS_BY_ID = (): Record<number, string> => ({
    1: optimisticTranslate('Internal'),
    2: optimisticTranslate('Confidential'),
    3: optimisticTranslate('Secret')
});

export enum CLASSIFICATION_LEVEL {
    Internal = 1,
    Confidential = 2,
    Secret = 3
}

const getClassification = (classificationObj: Record<string, string>) => (level: string) => ({
    level: Number(level),
    description: classificationObj[level]
});
export const classificationOptions = (): {
    level: number;
    description: string;
}[] => listFromObj(getClassification, MEMBER_CLASSIFICATION_LEVELS_BY_ID());

export const MEMBER_INVITATION_STATUSES = Object.freeze({
    NOT_SENT: '',
    PENDING: 'PENDING'
});

export const DOCUMENT_SECURITY = Object.freeze({
    FULL_ACCESS: 1,
    WATERMARK: 2,
    PRINT_DISABLED: 3,
    VIEW_ONLY: 4,
    NO_MOBILE_PRINTING: 5
});

export const DOCUMENT_TYPE = Object.freeze({
    CORPORATE: 'corporate',
    MINUTES: 'minutes-pdf',
    PRIVATE: 'private'
});

export const REDIRECTION_URLS = Object.freeze({
    SUPER_ADMIN: 'admin',
    CSM_ADMIN: 'csmadmin'
});

// about UX: arrow down is normal, arrow up is reverse
export enum SORTING_MODES {
    NORMAL = 'normal',
    REVERSE = 'reverse'
}

// logical order
export enum SORTING_ORDERS {
    ASC = 'ascending',
    DESC = 'descending'
}
